import { useState, useMemo } from "react";
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  TableSortLabel, Typography, Box,
} from "@mui/material";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { memberRate } from "../../../lib/progressCompare";
import UserAvatar from "../../../components/ui/UserAvatar";

const SORT_KEYS = {
  member: (r) => `${r.name || ""} ${r.surname || ""}`.toLowerCase(),
  daysActiveCount: (r) => r.daysActiveCount || 0,
  perfectDaysCount: (r) => r.perfectDaysCount || 0,
  totalValue: (r) => r.totalValue || 0,
  completionRate: memberRate,
};

export default function MemberStatsTable({ userStats }) {
  const { t } = useScriptMode();
  const [sortKey, setSortKey] = useState("completionRate");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = useMemo(() => {
    const list = Array.isArray(userStats) ? userStats.slice() : [];
    const fn = SORT_KEYS[sortKey] || SORT_KEYS.completionRate;
    list.sort((a, b) => {
      const va = fn(a), vb = fn(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [userStats, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "member" ? "asc" : "desc");
    }
  };

  if (sorted.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          {t("manage_stats_no_data")}
        </Typography>
      </Box>
    );
  }

  const sortableHeader = (key, label) => (
    <TableCell key={key}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDir : "asc"}
        onClick={() => handleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {sortableHeader("member", t("manage_stats_table_member"))}
          {sortableHeader("daysActiveCount", t("manage_stats_table_active_days"))}
          {sortableHeader("perfectDaysCount", t("manage_stats_table_perfect_days"))}
          {sortableHeader("totalValue", t("manage_stats_table_total_value"))}
          {sortableHeader("completionRate", t("manage_stats_table_completion"))}
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((r) => (
          <TableRow key={r.actorId} hover>
            <TableCell>
              <div className="flex items-center gap-2.5 min-w-0">
                <UserAvatar person={r} size={32} />
                <div className="min-w-0">
                  <div className="font-SemiBold text-sm">
                    {r.name} {r.surname}
                  </div>
                  <div className="text-xs text-terakkiNeutral">{r.email}</div>
                </div>
              </div>
            </TableCell>
            <TableCell>{r.daysActiveCount || 0}</TableCell>
            <TableCell>{r.perfectDaysCount || 0}</TableCell>
            <TableCell>
              {r.totalValue || 0} / {r.totalGoalValue || 0}
            </TableCell>
            <TableCell>%{memberRate(r)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
